/**
 * usePropertyAccess
 *
 * Per-property access for the CURRENTLY SELECTED property. Owner and member are
 * per-property roles: a user can OWN one house (full access) and be a view-only
 * member of another. House-data edit surfaces (devices, alarms, rooms, tasks,
 * property profile, dashboard editing, …) should gate on `canEdit` from here —
 * NOT on a global account flag — so switching properties changes what's editable.
 *
 * The database (RLS) enforces the same per-property rule; this keeps the UI honest.
 * Defaults to editable while resolving so an owner's controls never flicker away.
 */
import { useQuery } from '@tanstack/react-query';
import { useSelectedProperty } from '@/features/properties/hooks/useSelectedProperty';
import { getPropertyAccess, type PropertyRole } from '@/services/supabase/homeownerMembers.service';

export function usePropertyAccess(): {
  role: PropertyRole;
  isOwner: boolean;
  canEdit: boolean;
  isResolved: boolean;
} {
  const { selectedPropertyId } = useSelectedProperty();

  const { data, isSuccess } = useQuery({
    queryKey: ['propertyAccess', selectedPropertyId],
    queryFn: () => getPropertyAccess(selectedPropertyId as string),
    enabled: !!selectedPropertyId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    role: data?.role ?? 'none',
    isOwner: data?.isOwner ?? false,
    canEdit: data ? data.canEdit : true,
    isResolved: isSuccess,
  };
}
